import type { GridProps, HTMLChakraProps } from '@chakra-ui/react';
import { Box, Grid, Flex, Text, Link, VStack, Image, HStack } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useState } from 'react';

import type { CustomLinksGroup } from 'types/footerLinks';

import config from 'configs/app';
import type { ResourceError } from 'lib/api/resources';
import useFetch from 'lib/hooks/useFetch';
import useTranslation from 'lib/hooks/useTranslation';
import Skeleton from 'ui/shared/chakra/Skeleton';
import { CONTENT_MAX_WIDTH } from 'ui/shared/layout/utils';
import NetworkAddToWallet from 'ui/shared/NetworkAddToWallet';

import FooterLinkItem from './FooterLinkItem';
import IntTxsIndexingStatus from './IntTxsIndexingStatus';

const MAX_LINKS_COLUMNS = 4;

const linksDatas = [
  // {
  //   title: "About Us",
  //   pos: "vertical",
  //   links: [
  //     {
  //       text: "Whitepaper",
  //       icon: "files/json",
  //       iconSize: "18px",
  //       url: "https://strmchain.com/download/Whitepaper.pdf"
  //     }
  //   ]
  // },
  {
    title: "Let's Connect",
    pos: "horizontal",
    links: [
      {
        text: " ",
        icon: "social/twitter_filled",
        iconSize: "56px",
        url: "https://x.com/STRM_Chain"
      },
      {
        text: " ",
        icon: "social/telegram_filled",
        iconSize: "56px",
        url: "https://t.me/StreamChain_Official_Community"
      },
      {
        text: " ",
        icon: "email",
        iconSize: "56px",
        url: "mailto:support@strmchain.com"
      },
      // {
      //   text: " ",
      //   icon: "social/discord_filled",
      //   iconSize: "18px",
      //   url: "https://discord.gg/streamcoin"
      // },
      // {
      //   text: " ",
      //   icon: "social/github_filled",
      //   iconSize: "18px",
      //   url: "https://github.com/streamcoin"
      // },
      // {
      //   text: " ",
      //   icon: "social/medium_filled",
      //   iconSize: "24px",
      //   url: "https://medium.com/streamcoin"
      // }
    ]
  }
]

const Footer = () => {
  const fetch = useFetch();
  const { t } = useTranslation();
  const [ isClient, setIsClient ] = useState(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  const { isPlaceholderData, data: linksData } = useQuery<unknown, ResourceError<unknown>, Array<CustomLinksGroup>>({
    queryKey: [ 'footer-links' ],
    queryFn: async() => {
      return linksDatas;
    },//fetch(config.UI.footer.links || '', undefined, { resource: 'footer-links' }),
    enabled: Boolean(linksDatas),
    staleTime: Infinity,
    placeholderData: [],
  });

  

  const colNum = isPlaceholderData ? 1 : Math.min(linksData?.length || Infinity, MAX_LINKS_COLUMNS);
  const renderNetworkInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    return (
      <Flex
        gridArea={ gridArea }
        flexWrap="wrap"
        columnGap={ 8 }
        rowGap={ 6 }
        mb={{ base: 5, lg: 10 }}
        _empty={{ display: 'none' }}
      >
        { !config.UI.indexingAlert.intTxs.isHidden && <IntTxsIndexingStatus/> }
        <NetworkAddToWallet/>
      </Flex>
    );
  }, []);

  const renderProjectInfo = React.useCallback((gridArea?: GridProps['gridArea']) => {
    const projectDescription = isClient ?
      t('footer.project_description') :
      'The Stream Chain 2.0 project addresses fundamental problems in existing industry ecosystems and presents the perfect solution for building a new global blockchain aviation ecosystem through protocol architecture, global aviation economy, and infinitely scalable expansion capabilities.';

    return (
      <Box gridArea={ gridArea } color="black">
        <Flex direction="column" rowGap={ 2 } fontSize="sm" lineHeight={ 5 } alignItems="flex-start" fontWeight="bold">
          <Image
            src="/static/logo_name_white.png"
            alt="Hero Logo"
            maxH="40px"
          />
          <Text fontSize="xs">
            { projectDescription }
          </Text>
        </Flex>
      </Box>
    );
  }, [ t, isClient ]);

  const containerProps: HTMLChakraProps<'div'> = {
    as: 'footer',
    borderTopWidth: '1px',
    borderTopColor: 'solid',
  };

  const contentProps: GridProps = {
    px: { base: 4, lg: config.UI.navigation.layout === 'horizontal' ? 6 : 12, '2xl': 6 },
    py: { base: 4, lg: 8 },
    gridTemplateColumns: { base: '1fr', lg: 'minmax(auto, 470px) 1fr' },
    columnGap: { lg: '32px', xl: '100px' },
    maxW: `${ CONTENT_MAX_WIDTH }px`,
    m: '0 auto',
  };

  const renderRecaptcha = (gridArea?: GridProps['gridArea']) => {
    if (!config.services.reCaptchaV2.siteKey) {
      return <Box gridArea={ gridArea }/>;
    }

    const recaptchaText = isClient ? t('footer.recaptcha_text') : 'This site is protected by reCAPTCHA and the Google';
    const privacyPolicy = isClient ? t('footer.privacy_policy') : 'Privacy Policy';
    const andText = isClient ? t('footer.and') : 'and';
    const termsOfService = isClient ? t('footer.terms_of_service') : 'Terms of Service';
    const applyText = isClient ? t('footer.apply') : 'apply.';

    return (
      <Box gridArea={ gridArea } fontSize="xs" lineHeight={ 5 } mt={ 6 } color="text">
        <span>{ recaptchaText } </span>
        <Link href="https://policies.google.com/privacy" isExternal>{ privacyPolicy }</Link>
        <span> { andText } </span>
        <Link href="https://policies.google.com/terms" isExternal>{ termsOfService }</Link>
        <span> { applyText }</span>
      </Box>
    );
  };

  if (linksDatas) {
    return (
      <Box { ...containerProps }>
        <Grid { ...contentProps }>
          <div>
            { renderNetworkInfo() }
            { renderProjectInfo() }
            { renderRecaptcha() }
          </div>

          <Grid
            gap={{ base: 6, lg: colNum === MAX_LINKS_COLUMNS + 1 ? 2 : 8, xl: 12 }}
            gridTemplateColumns={{
              base: 'repeat(auto-fill, 160px)',
              lg: `repeat(${ colNum }, 135px)`,
              xl: `repeat(${ colNum }, 160px)`,
            }}
            justifyContent={{ lg: 'flex-end' }}
            mt={{ base: 8, lg: 0 }}
          >
            {
              ([
                ...(linksDatas || []),
              ])
                .slice(0, colNum)
                .map(linkGroup => (
                  <Box key={ linkGroup.title }>
                    <Skeleton fontWeight={ 500 } fontSize="18px" mb={ 3 } display="inline-block" isLoaded={ !isPlaceholderData }>{ linkGroup.title }</Skeleton>
                    { linkGroup.pos === 'vertical' ? (
                      <VStack spacing={ 1 } alignItems="start">
                        { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.url } isLoading={ isPlaceholderData }/>) }
                      </VStack>
                    ) : (
                      <HStack spacing={ 1 } alignItems="start">
                        { linkGroup.links.map(link => <FooterLinkItem { ...link } key={ link.url } isLoading={ isPlaceholderData }/>) }
                      </HStack>
                    ) }
                  </Box>
                ))
            }
          </Grid>
        </Grid>
      </Box>
    );
  }

  return null;
};

export default React.memo(Footer);
